import { useState, useEffect, useRef } from "react";
import { Link, useLocation, NavLink } from "react-router-dom";
import useDarkMode from "./hooks/useDarkMode";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dark, setDark] = useDarkMode();
  const location = useLocation();
  const panelRef = useRef(null);
  const firstLinkRef = useRef(null);

  // Закрываем меню при смене маршрута
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // ESC + trap-фокус
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
      if (menuOpen && e.key === "Tab" && panelRef.current) {
        const nodes = panelRef.current.querySelectorAll(
          "a,button,[tabindex]:not([tabindex='-1'])"
        );
        const focusables = Array.from(nodes);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  useEffect(() => {
    if (menuOpen && firstLinkRef.current) firstLinkRef.current.focus();
  }, [menuOpen]);

  // Базовые стили ссылок (desktop)
  const linkBase =
    "transition relative hover:text-purple-700 dark:hover:text-purple-300";

  // Активное состояние: «светится» градиентным текстом + мягкое свечение
  const activeGlow =
    "text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 " +
    "drop-shadow-[0_0_10px_rgba(168,85,247,0.7)] dark:drop-shadow-[0_0_10px_rgba(192,132,252,0.75)]";

  return (
    <header
      className="fixed top-0 left-0 w-full py-4 px-6 flex items-center 
                 bg-white/70 dark:bg-black/80 backdrop-blur-md shadow-lg
                 border-b border-violet-700/30 dark:border-violet-300/10 z-20"
    >
      {/* Skip link для клавиатуры */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-6 focus:top-2 focus:bg-white focus:text-gray-900 dark:focus:bg-gray-900 dark:focus:text-gray-100 focus:px-3 focus:py-2 focus:rounded-lg"
      >
        Перейти к контенту
      </a>

      {/* Логотип */}
      <Link
        to="/"
        className="flex items-center select-none leading-none"
        aria-label="На главную"
      >
        {/* Desktop / tablet: полный текст с градиентом (в обеих темах одинаковый) */}
        <span
          className="
            hidden sm:inline
            text-2xl sm:text-3xl font-black tracking-tight
            text-transparent bg-clip-text
            bg-gradient-to-r from-cyan-400 via-violet-600 to-violet-950
            drop-shadow
          "
        >
          ZMNCRAFT
        </span>

        {/* Mobile: только ZC, Z = голубой, C = тёмно-фиолетовый */}
        {/* Mobile: полное название, компактное по размерам */}
        <span
          className="
            sm:hidden
            text-[18px] xs:text-[20px]
            font-black tracking-tight
            text-transparent bg-clip-text
            bg-gradient-to-r from-cyan-400 via-violet-600 to-violet-950
            whitespace-nowrap
        "
        >
          ZMNCRAFT
        </span>

      </Link>


      {/* Меню по центру (desktop) */}
      <nav className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
        <ul className="flex space-x-6 text-violet-900 dark:text-violet-200 font-medium text-base lg:text-lg">
          {[
            { to: "/", label: "Главная" },
            { to: "/products", label: "Продукты" },
            { to: "/services", label: "Услуги" },
            { to: "/blog", label: "Блог" },
            { to: "/about", label: "О нас" },
            { to: "/contacts", label: "Контакты" },
            { to: "/cooperate", label: "Сотрудничество" },
          ].map((i) => (
            <li key={i.to}>
              <NavLink
                to={i.to}
                className={({ isActive }) =>
                  `${linkBase} ${isActive ? activeGlow : ""}`
                }
                end={i.to === "/"}
              >
                {i.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Переключатель темы — показываем только на desktop, в мобайле уедет в нижнюю часть меню */}
      <button
        onClick={() => setDark(!dark)}
        className="hidden md:inline-flex ml-4 p-2 rounded-lg border border-gray-300 dark:border-gray-700
                   hover:bg-gray-100 dark:hover:bg-gray-800 transition"
        title="Переключить тему"
        aria-pressed={dark}
      >
        {dark ? (
          <span role="img" aria-label="Светлая тема">☀️</span>
        ) : (
          <span role="img" aria-label="Тёмная тема">🌙</span>
        )}
      </button>

      {/* Бургер (mobile) */}
      <div className="md:hidden flex items-center ml-auto">
        <button
          className="focus:outline-none"
          aria-label="Открыть меню"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <svg width="32" height="32" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <rect y="5" width="24" height="2" rx="1" className="fill-violet-600 dark:fill-violet-400" />
            <rect y="11" width="24" height="2" rx="1" className="fill-fuchsia-600 dark:fill-fuchsia-400" />
            <rect y="17" width="24" height="2" rx="1" className="fill-violet-600 dark:fill-violet-400" />
          </svg>
        </button>
      </div>

      {/* Мобильное меню */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-end" role="dialog" aria-modal="true">
          <div
            ref={panelRef}
            className="w-64 h-screen bg-white dark:bg-gray-900 shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-800"
          >
            {/* Кнопка закрытия */}
            <div className="p-6">
              <button
                className="ml-auto block"
                aria-label="Закрыть меню"
                onClick={() => setMenuOpen(false)}
              >
                <svg width="28" height="28" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <line x1="4" y1="4" x2="16" y2="16" className="stroke-gray-900 dark:stroke-gray-100" strokeWidth="2" />
                  <line x1="16" y1="4" x2="4" y2="16" className="stroke-gray-900 dark:stroke-gray-100" strokeWidth="2" />
                </svg>
              </button>
            </div>

            {/* Ссылки — скроллятся, если не влезают */}
            <nav className="flex-1 overflow-y-auto px-6 pb-6">
              <ul className="flex flex-col items-center text-center space-y-6 text-gray-900 dark:text-gray-100 text-xl font-semibold">
                {[
                  { to: "/", label: "Главная", end: true },
                  { to: "/products", label: "Продукты" },
                  { to: "/services", label: "Услуги" },
                  { to: "/blog", label: "Блог" },
                  { to: "/about", label: "О нас" },
                  { to: "/contacts", label: "Контакты" },
                  { to: "/cooperate", label: "Сотрудничество" },
                ].map((i, idx) => (
                  <li key={i.to}>
                    <NavLink
                      to={i.to}
                      end={i.end}
                      ref={idx === 0 ? firstLinkRef : undefined}
                      onClick={() => setMenuOpen(false)}
                      className={({ isActive }) =>
                        isActive
                          ? "text-transparent bg-clip-text bg-gradient-to-r from-violet-500 via-fuchsia-500 to-indigo-500 drop-shadow-[0_0_10px_rgba(192,132,252,0.75)]"
                          : undefined
                      }
                    >
                      {i.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Кнопка темы — всегда видна (липнет к низу, но с отступом) */}
            <div className="sticky bottom-0 z-10 px-6 pt-4 pb-6 mb-6 bg-white/85 dark:bg-gray-900/85 backdrop-blur border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => setDark(!dark)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl 
               border border-gray-300 dark:border-gray-700
               hover:bg-gray-100 dark:hover:bg-gray-800 transition"
                aria-pressed={dark}
                title="Переключить тему"
              >
                {dark ? (
                  <>
                    <span role="img" aria-label="Светлая тема">☀️</span>
                    <span>Светлая тема</span>
                  </>
                ) : (
                  <>
                    <span role="img" aria-label="Тёмная тема">🌙</span>
                    <span>Тёмная тема</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* затемнение и клик-наружу */}
          <button
            className="flex-1 h-full bg-black/40 dark:bg-black/60"
            aria-label="Закрыть меню"
            onClick={() => setMenuOpen(false)}
          />
        </div>
      )}
    </header>
  );
}
